import React from "react"
import { useTranslation } from "react-i18next"

import { useActiveOrganization, useAuthentication } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import type { Site } from "@/models/site"
import { useSigmaWorkbook } from "@/services/sigma"
import type { SigmaWorkbook } from "@/services/sigma"
import { useSigmaWorkbookEmbedUrl } from "@/services/sigma/use-sigma-workbook-embed-url"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import { getSelectableLanguageCode } from "@/utils/i18n"

import { Box, Skeleton } from "@mui/material"

interface SigmaReportProps {
  reportTitle: string
  siteId?: Site["id"]
  workbookId: SigmaWorkbook["workbookId"] | undefined
}

export const SigmaReport: React.FC<SigmaReportProps> = ({
  reportTitle,
  siteId,
  workbookId,
}) => {
  const { activeUser } = useAuthentication()
  const { activeOrganization } = useActiveOrganization()
  const { i18n } = useTranslation()
  const { invalidateQueries } = useInvalidateQueries()
  const { sigmaWorkbookData } = useSigmaWorkbook(workbookId)
  const activeOrganizationId = activeOrganization?.id
  const embedLanguage = getSelectableLanguageCode(i18n.resolvedLanguage)

  const { sigmaWorkbookEmbedUrlData, sigmaWorkbookEmbedUrlIsSuccess } =
    useSigmaWorkbookEmbedUrl({
      options: {
        email: activeUser?.email,
        lng: embedLanguage,
        organizationId: activeOrganizationId,
        siteId,
        workbookUrlId: sigmaWorkbookData?.workbookUrlId,
      },
    })

  const [isReportLoading, setIsReportLoading] = React.useState(true)
  const previousOrganizationIdRef = React.useRef(activeOrganizationId)
  const previousEmbedLanguageRef = React.useRef(embedLanguage)

  React.useEffect(() => {
    if (previousEmbedLanguageRef.current === embedLanguage) {
      return
    }

    previousEmbedLanguageRef.current = embedLanguage
    setIsReportLoading(true)
  }, [embedLanguage])

  React.useEffect(() => {
    if (
      !activeOrganizationId ||
      previousOrganizationIdRef.current === activeOrganizationId
    ) {
      previousOrganizationIdRef.current = activeOrganizationId
      return
    }

    previousOrganizationIdRef.current = activeOrganizationId
    setIsReportLoading(true)
    invalidateQueries([
      createQueryKey(ApiQueryName.SigmaWorkbookEmbedUrl, "getOne"),
      createQueryKey(ApiQueryName.SigmaWorkbooks, "getMany"),
    ])
  }, [activeOrganizationId, invalidateQueries])

  const iframeKey = `${activeOrganizationId ?? ""}-${workbookId ?? ""}-${embedLanguage}`

  return (
    <Box
      aria-label={reportTitle}
      component="section"
      sx={{
        bottom: 0,
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
      }}
    >
      {isReportLoading && (
        <Skeleton
          data-testid="sigma-report-skeleton"
          height="auto"
          sx={{
            bottom: 0,
            left: 0,
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: 1,
          }}
          variant="rounded"
          width="auto"
        />
      )}
      {sigmaWorkbookEmbedUrlIsSuccess && (
        <iframe
          key={iframeKey}
          height="100%"
          onLoad={() => {
            setIsReportLoading(false)
          }}
          src={sigmaWorkbookEmbedUrlData}
          style={{
            border: "none",
            visibility: isReportLoading ? "hidden" : "visible",
          }}
          title={reportTitle}
          width="100%"
        />
      )}
    </Box>
  )
}
